import { IPromotionBenefit } from 'types';
import { t } from 'i18n';
import { CashColored, VoucherColored } from '@teko/ds-icon';
import { styled, LazyImage, Flex, Body } from '@teko/ds-desktop';
import { formatCurrency } from '@teko/teko-ecommerce';
import { useTenantConfig } from 'contexts';

interface IBenefit {
  name?: string;
  image?: string;
  quantity?: number;
  icon?: React.ReactNode;
}

interface IPromotionBenefitInfoProps {
  gifts?: IBenefit[];
  promotionBenefit?: IPromotionBenefit;
  backgroundColor?: string;
}

export const PromotionBenefitInfo: React.FC<IPromotionBenefitInfoProps> = ({
  promotionBenefit,
  gifts,
  backgroundColor,
}) => {
  const { numberDisplayFormat } = useTenantConfig();
  const getBenefits = (benefit: IPromotionBenefit) => {
    let count = 0;
    const benefits = [];

    const { discount, voucher, gifts } = benefit;
    const discountValue = discount?.flat || discount?.percent || 0;
    if (discount && discountValue > 0) {
      count++;
      benefits.push({
        icon: (
          <StyledBgPromotion bgColor="#E7FBED" size="40px">
            <CashColored size={30} />
          </StyledBgPromotion>
        ),
        name: `${t('Giảm ngay')} ${
          discount?.flat
            ? formatCurrency(discountValue, numberDisplayFormat)
            : `${discountValue}%` +
              (discount.maxAmount
                ? ` ${t('Giảm tối đa')} ${formatCurrency(
                    discount.maxAmount,
                    numberDisplayFormat,
                  )}`
                : '')
        }`,
      });
    }
    if (voucher) {
      count++;
      benefits.push({
        icon: (
          <StyledBgPromotion bgColor="#FFF2F0" size="40px">
            <VoucherColored size={30} />
          </StyledBgPromotion>
        ),
        name: voucher.name,
        quantity: voucher.quantity,
      });
    }
    if (gifts && !!gifts.length) {
      count++;
      gifts.forEach((gift) => {
        benefits.push({
          image: gift.imageUrl,
          name: gift.name,
          quantity: gift.quantity,
        });
      });
    }
    return { numberOfBenefit: count, benefits };
  };

  const benefits: IBenefit[] = gifts
    ? gifts
    : promotionBenefit
    ? getBenefits(promotionBenefit).benefits
    : [];

  return benefits.length ? (
    <DetailInfoItem backgroundColor={backgroundColor}>
      {benefits.map(({ name, image, quantity, icon }, index) => (
        <Flex width="100%" align="center" marginBottom="s4" key={index}>
          {icon ? (
            <>{icon}</>
          ) : (
            <LazyImage src={image || ''} width={40} height={40} />
          )}
          <Body level={1} line={2} marginLeft="s8">
            {quantity && quantity > 1 && (
              <span style={{ color: '#2b2b2b' }}>{`${quantity} x `}</span>
            )}
            {name}
          </Body>
        </Flex>
      ))}
    </DetailInfoItem>
  ) : null;
};

interface StyledBgPromotionProps {
  bgColor: string;
  size?: string;
}

const DetailInfoItem = styled.div<
  Pick<IPromotionBenefitInfoProps, 'backgroundColor'>
>(
  ({
    backgroundColor = '#f8f8fc',
  }: Pick<IPromotionBenefitInfoProps, 'backgroundColor'>) => `
  border-radius: 8px;
  background-color: ${backgroundColor};
  padding: 12px 16px;
  margin-top: 12px;
`,
);

const StyledBgPromotion = styled.div<StyledBgPromotionProps>(
  ({ bgColor, size = '125px' }: StyledBgPromotionProps) => `
  width: ${size};
  height: ${size};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${bgColor};
  border-radius: 4px;
`,
);
